import { useLoad } from "../api";
import { HowTo, Later, PageHead, Tip, useStatus } from "../ui";
import { ControlCard, EventsCard, MonitorsCard, OnAirCard, OutputsCard, RecentCard } from "./widgets";
import { Hosts, PublicSettings, Stats, Webhooks } from "./station/Extras";

const TABS: [string, string][] = [
  ["uebersicht", "Übersicht"],
  ["sendung", "Sendung"],
  ["studio", "Studio"],
  ["streams", "Streams"],
  ["moderatoren", "Moderatoren"],
  ["webhooks", "Webhooks"],
  ["statistik", "Statistik"],
  ["oeffentlich", "Öffentliche Seite"],
];

function StationHead() {
  const { data: st } = useLoad("/api/station");
  const status = useStatus();
  const e = status?.engine;
  const live = e?.on_air?.kind === "stream";
  return (
    <PageHead
      title={st?.name ?? "Sender"}
      sub={
        <span className="row wrap">
          {live ? <span className="badge live">LIVE</span> : e?.state === "läuft" ? <span className="badge ok">AUTO</span> : <span className="badge bad">OFF</span>}
          <span className="mono">/{st?.slug}</span>
          <span>{st?.timezone}</span>
          {st?.slogan && <span>· {st.slogan}</span>}
        </span>
      }
    >
      <a className="btn" href="#/einstellungen/sender">Bearbeiten</a>
    </PageHead>
  );
}

function Overview() {
  const status = useStatus();
  const e = status?.engine;
  const { data: cats } = useLoad<any[]>("/api/categories");
  const { data: clocks } = useLoad<any[]>("/api/clocks");
  const { data: grid } = useLoad<any>("/api/grid");
  const songsInCats = (cats ?? []).filter((c) => c.kind === "music").reduce((a, c) => a + c.songs, 0);
  const steps: [string, boolean, string, string][] = [
    ["Musik in Kategorien", songsInCats > 0, `${songsInCats} Songs in Musik-Kategorien`, "#/bibliothek"],
    ["Uhren bauen", (clocks ?? []).some((c) => c.elements?.length), `${clocks?.length ?? 0} Uhren`, "#/plan/uhren"],
    ["Woche bemalen", (grid?.cells ?? []).some((c: any) => c.clock_id), (grid?.cells ?? []).length ? "Wochenraster belegt" : "Wochenraster leer", "#/plan/wochenraster"],
    ["On Air", e?.state === "läuft" && (e?.outputs ?? []).some((o: any) => o.state === "verbunden"), "Ausgang verbunden, Engine läuft", "#/einstellungen/ausgaenge"],
  ];
  return (
    <div className="grid g2">
      <div className="stack">
        <div className="card">
          <h2>Einrichtung</h2>
          <p className="muted" style={{ marginTop: 0 }}>Songs → Kategorien → Uhren → Wochenraster → Sendeplan → Sendung. Solange die Kette nicht vollständig ist, spielt die Engine die Rotation über die Kategorien.</p>
          {steps.map(([t, done, note, href], i) => (
            <div key={t} className="row" style={{ padding: "6px 0" }}>
              <span className={`badge ${done ? "ok" : ""}`}>{done ? "✓" : i + 1}</span>
              <a href={href} style={{ fontWeight: 600, color: "var(--ink)" }}>{t}</a>
              <span className="muted small">{note}</span>
            </div>
          ))}
        </div>
        <OnAirCard engine={e} />
      </div>
      <div className="stack">
        <RecentCard recent={status?.recent} />
      </div>
    </div>
  );
}

function Broadcast() {
  const status = useStatus();
  const e = status?.engine;
  return (
    <div>
      <Tip>Sanfter Neustart: Plan und Einstellungen werden neu geladen, Hörer bleiben verbunden. Harter Neustart: der Engine-Prozess startet neu; Hörer des Test-Mounts hören solange die Pausenschleife.</Tip>
      <div className="grid g2">
        <div className="stack">
          <OnAirCard engine={e} />
          <ControlCard engine={e} />
        </div>
        <div className="stack">
          <OutputsCard engine={e} />
          <MonitorsCard engine={e} />
          <EventsCard limit={15} />
        </div>
      </div>
    </div>
  );
}

function Streams() {
  const { data: outs } = useLoad<any[]>("/api/outputs");
  const origin = window.location.origin;
  return (
    <div className="card">
      <h2>Hörer-Links</h2>
      <p className="muted" style={{ marginTop: 0 }}>Die Adressen, unter denen Hörer die Ausgänge empfangen. Ein eigener Hörer-Link lässt sich je Ausgang eintragen.</p>
      <table className="list">
        <thead><tr><th>Ausgang</th><th>Format</th><th>Hörer-Link</th></tr></thead>
        <tbody>
          {(outs ?? []).map((o) => {
            const url = o.listen_url || (o.host === "127.0.0.1" || o.host === "localhost" ? `${origin}/stream${o.mount}` : `http://${o.host}:${o.port}${o.mount}`);
            return (
              <tr key={o.id}>
                <td><b>{o.name}</b> {!o.enabled && <span className="badge">aus</span>}</td>
                <td className="small">{o.format.toUpperCase()} {o.bitrate} kbit/s · {o.channels === 1 ? "Mono" : "Stereo"} · {o.samplerate / 1000} kHz</td>
                <td><a className="mono" href={url} target="_blank" rel="noreferrer">{url}</a></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 16 }}>
      </div>
    </div>
  );
}

export function Station({ parts }: { parts: string[] }) {
  const tab = parts[1] ?? "uebersicht";
  let body;
  switch (tab) {
    case "sendung": body = <Broadcast />; break;
    case "streams": body = <Streams />; break;
    case "studio": body = <Later stage={7} title="Studio" points={["Live-Studio im Browser", "Handy-Mikro, Call Screen", "MIDI-Steuerung"]} />; break;
    case "moderatoren": body = <Hosts />; break;
    case "webhooks": body = <Webhooks />; break;
    case "statistik": body = <Stats />; break;
    case "oeffentlich": body = <PublicSettings />; break;
    default: body = <Overview />;
  }
  return (
    <div>
      <StationHead />
      <div className="tabs">
        {TABS.map(([k, l]) => (
          <a key={k} href={`#/sender/main/${k}`} className={k === tab ? "active" : ""}>{l}</a>
        ))}
      </div>
      <HowTo text="Der Senderbereich bündelt alles zu einem Sender: was läuft, wie er sendet, wer moderiert." chapter="sender" />
      {body}
    </div>
  );
}
